import {
  ContactType,
  PersonPaginationSort,
  PersonStatus,
} from '../gql/graphql';
import { getPaginatedNodes, toPagination } from './utils';

import { graphql } from '../gql';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_ENTITIES_BROKERS_OPTIONS = graphql(`
  query getBrokerOptions(
    $filters: PersonFilterInput
    $pagination: PaginationRequest
    $sort: PersonPaginationSort
  ) {
    persons(filters: $filters, pagination: $pagination, sort: $sort) {
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
      edges {
        cursor
        node {
          uuid
          createdAt
          firstName
          lastName
          status
          agent
          title
          contacts {
            edges {
              cursor
              node {
                uuid
                type
                value
                source
                primary
              }
            }
          }
        }
      }
    }
  }
`);

export type Broker = {
  uuid: string;
  value: string;
  email: string;
  name: string;
  phone: string;
  firstName?: string | null;
  lastName?: string | null;
};
export function useGetBrokerOptions({
  pageNumber = 0,
  pageSize = 1000,
  uuid,
  isAgency = true,
  sort,
  firstName,
  enabled,
  brokerageUuid,
}: {
  pageNumber?: number;
  pageSize?: number;
  uuid?: string;
  firstName?: string;
  isAgency?: boolean;
  enabled?: boolean;
  sort?: PersonPaginationSort;
  brokerageUuid?: string;
}) {
  const filters: {
    agent: boolean;
    fullName?: string;
    brokerages?: string[];
    status?: PersonStatus;
  } = {
    agent: isAgency,
  };
  if (firstName) {
    filters['fullName'] = firstName;
  }
  if (brokerageUuid) {
    filters['brokerages'] = [brokerageUuid];
  }
  const { loading, error, data, fetchMore } = useQuery(
    GET_ENTITIES_BROKERS_OPTIONS,
    {
      variables: {
        pagination: toPagination(pageNumber, pageSize),
        sort,
        filters: filters,
      },
      skip: !enabled,
      fetchPolicy: 'network-only',
    }
  );
  const brokers = useMemo(() => {
    if (!data || !data.persons || !data.persons.edges) return [];

    return getPaginatedNodes(data.persons)
      .map((edge) => {
        const isInactive = edge.status === 'INACTIVE';
        const baseName = `${edge?.firstName} ${edge?.lastName}`;
        const displayName = isInactive ? `${baseName} (Inactive)` : baseName;

        return {
          id: edge?.uuid ?? '--',
          uuid: edge?.uuid ?? '--',
          firstName: edge?.firstName,
          lastName: edge?.lastName,
          name: baseName,
          value: edge?.uuid ?? '--',
          label: displayName,
          status: edge.status || undefined,
          isInactive,
          email:
            edge.contacts.edges?.find(
              (contact) => contact.node.type === ContactType.Email
            )?.node.value ?? '',
          phone:
            edge.contacts.edges?.find(
              (contact) => contact.node.type === ContactType.Phone
            )?.node.value ?? '',
          title: edge?.title,
        };
      })
      .sort((a, b) => {
        const firstNameA = (a.firstName || '').toLowerCase();
        const firstNameB = (b.firstName || '').toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
  }, [data]);
  const hasNextPage = data?.persons?.pageInfo?.hasNextPage ?? false;

  return {
    error,
    loading,
    data,
    brokers,
    fetchMore,
    hasNextPage,
  };
}
